import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { FitmentChecker } from "@/components/FitmentChecker";
import { DealerBadge } from "@/components/DealerBadge";
import { MapPin, Eye, Zap, MessageSquare, Flag, Truck, Package, Edit, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const CAT_LABEL = { wheels: "Wheels Only", tires: "Tires Only", set: "Wheels + Tires Set" };
const FULFILL = { pickup: "Local Pickup", shipping: "Shipping", both: "Pickup or Shipping" };

const SpecRow = ({ label, value }) => value ? (
  <div className="flex justify-between items-center py-2.5 border-b border-border">
    <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
    <span className="spec-block text-sm text-blaze font-bold">{value}</span>
  </div>
) : null;

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [active, setActive] = useState(0);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [msgText, setMsgText] = useState("");

  const load = () => api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch(() => setListing(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (listing === false) return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-muted-foreground">Listing not found.</div>;
  if (!listing) return <div className="max-w-[1500px] mx-auto px-6 py-10"><div className="h-96 bg-[#141414] border border-border rounded-sm animate-pulse" /></div>;

  const isOwner = user && user.user_id === listing.seller_id;
  const photos = listing.photos || [];

  const contactSeller = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      const conv = await api.post("/conversations", { listing_id: listing.id });
      if (msgText.trim()) {
        await api.post(`/conversations/${conv.data.id}/messages`, { text: msgText });
      }
      toast.success("Message sent");
      navigate(`/messages/${conv.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start conversation");
    }
  };

  const submitReport = async () => {
    if (!user) { navigate("/login"); return; }
    if (!reportReason) { toast.error("Select a reason"); return; }
    try {
      await api.post(`/listings/${listing.id}/report`, { reason: reportReason, details: reportDetails });
      toast.success("Report submitted. Our team will review it.");
      setReportReason(""); setReportDetails("");
    } catch {
      toast.error("Could not submit report");
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this listing?")) return;
    await api.delete(`/listings/${listing.id}`);
    toast.success("Listing deleted");
    navigate("/dashboard");
  };

  return (
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Gallery */}
        <div className="lg:col-span-3">
          <div className="relative bg-[#1C1C1C] border border-border rounded-sm overflow-hidden aspect-[4/3]">
            {listing.boosted && (
              <div className="absolute top-3 left-3 z-10 bg-blaze text-white text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm flex items-center gap-1">
                <Zap className="w-3.5 h-3.5" /> Boosted
              </div>
            )}
            {photos[active] ? (
              <img data-testid="listing-main-photo" src={fileUrl(photos[active])} alt={listing.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Package className="w-16 h-16" /></div>
            )}
          </div>
          {photos.length > 1 && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {photos.map((p, i) => (
                <button key={i} onClick={() => setActive(i)} className={`w-20 h-20 rounded-sm overflow-hidden border ${i === active ? "border-blaze" : "border-border"}`}>
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {listing.proof_photo && (
            <div className="mt-3">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-emerald-400 mb-2">
                <ShieldCheck className="w-3.5 h-3.5" /> Fitment Proof Photo
              </div>
              <img data-testid="proof-photo" src={fileUrl(listing.proof_photo)} alt="fitment proof" className="w-40 h-40 object-cover rounded-sm border border-emerald-600/40" />
            </div>
          )}

          <div className="mt-4">
            <FitmentChecker listing={listing} />
          </div>
        </div>
        {/* Details */}
        <div className="lg:col-span-2 space-y-5">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-widest text-blaze">{CAT_LABEL[listing.category]}</span>
            <h1 data-testid="listing-title" className="font-heading font-900 text-2xl sm:text-3xl leading-tight mt-1 tracking-tight">{listing.title}</h1>
            <div className="text-sm text-muted-foreground mt-1 uppercase tracking-wide">{listing.brand}{listing.model ? ` · ${listing.model}` : ""}</div>
          </div>

          <div className="flex items-end justify-between">
            <div className="font-heading font-900 text-4xl text-white" data-testid="listing-price">${listing.price?.toLocaleString()}</div>
            <div className="text-right text-xs text-muted-foreground">
              <div className="flex items-center gap-1 justify-end"><Eye className="w-3.5 h-3.5" /> {listing.views} views</div>
              <div className="mt-1">{listing.condition}</div>
            </div>
          </div>

          {/* Fitment spec block */}
          <div className="bg-[#141414] border border-border rounded-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Fitment Specs</div>
              {listing.fitment_verified && (
                <span data-testid="fitment-verified-badge" className="inline-flex items-center gap-1 bg-emerald-600/20 text-emerald-400 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border border-emerald-600/40">
                  <ShieldCheck className="w-3 h-3" /> Verified Fit
                </span>
              )}
            </div>
            <SpecRow label="Bolt Pattern" value={listing.bolt_pattern} />
            <SpecRow label="Diameter" value={listing.diameter ? `${listing.diameter}"` : null} />
            <SpecRow label="Width" value={listing.width ? `${listing.width}"` : null} />
            <SpecRow label="Offset" value={listing.offset ? `ET ${listing.offset}` : null} />
            <SpecRow label="Tire Size" value={listing.tire_size} />
            <SpecRow label="Quantity" value={`${listing.quantity} pc`} />
            <SpecRow label="Fulfillment" value={FULFILL[listing.fulfillment]} />
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground pt-3">
              <MapPin className="w-4 h-4 text-blaze" /> {listing.location}
              {listing.fulfillment !== "pickup" && <Truck className="w-4 h-4 ml-2 text-blaze" />}
            </div>
          </div>

          {listing.description && (
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Description</div>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{listing.description}</p>
            </div>
          )}

          {/* Seller */}
          <Link to={`/seller/${listing.seller_id}`} data-testid="listing-seller-link" className="flex items-center gap-3 p-3 bg-[#141414] border border-border rounded-sm hover:border-blaze/50 transition-colors">
            <Avatar className="w-10 h-10 border border-border"><AvatarFallback className="bg-secondary">{listing.seller_name?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Seller</div>
              <div data-testid="listing-seller-name" className="font-heading font-800 text-sm">{listing.seller_name}</div>
              {listing.featured && <DealerBadge className="mt-0.5" />}
            </div>
            <span className="ml-auto text-xs text-blaze">View profile →</span>
          </Link>

          {/* Actions */}
          {isOwner ? (
            <div className="grid grid-cols-3 gap-2">
              <Button data-testid="owner-boost-btn" onClick={() => navigate(`/boost/${listing.id}`)} className="bg-blaze hover:bg-blaze-hover text-white rounded-sm font-bold gap-1.5"><Zap className="w-4 h-4" /> Boost</Button>
              <Button data-testid="owner-edit-btn" onClick={() => navigate(`/edit/${listing.id}`)} variant="outline" className="border-border bg-transparent rounded-sm gap-1.5"><Edit className="w-4 h-4" /> Edit</Button>
              <Button data-testid="owner-delete-btn" onClick={del} variant="outline" className="border-border bg-transparent rounded-sm text-destructive gap-1.5"><Trash2 className="w-4 h-4" /> Delete</Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Dialog>
                <DialogTrigger asChild>
                  <Button data-testid="contact-seller-btn" className="flex-1 bg-blaze hover:bg-blaze-hover text-white rounded-sm font-bold gap-2 h-11"><MessageSquare className="w-4 h-4" /> Contact Seller</Button>
                </DialogTrigger>
                <DialogContent className="bg-[#141414] border-border">
                  <DialogHeader><DialogTitle className="font-heading uppercase">Message {listing.seller_name}</DialogTitle></DialogHeader>
                  <Textarea data-testid="contact-message-input" value={msgText} onChange={(e) => setMsgText(e.target.value)} placeholder={`Hi, is this "${listing.title}" still available?`} className="bg-[#0A0A0A] border-border rounded-sm min-h-24" />
                  <DialogFooter><Button data-testid="contact-send-btn" onClick={contactSeller} className="bg-blaze hover:bg-blaze-hover rounded-sm font-bold">Send Message</Button></DialogFooter>
                </DialogContent>
              </Dialog>
              <Dialog>
                <DialogTrigger asChild>
                  <Button data-testid="report-btn" variant="outline" size="icon" className="border-border bg-transparent rounded-sm h-11 w-11 hover:text-destructive"><Flag className="w-4 h-4" /></Button>
                </DialogTrigger>
                <DialogContent className="bg-[#141414] border-border">
                  <DialogHeader><DialogTitle className="font-heading uppercase">Report Listing</DialogTitle></DialogHeader>
                  <Select value={reportReason} onValueChange={setReportReason}>
                    <SelectTrigger data-testid="report-reason-select" className="bg-[#0A0A0A] border-border rounded-sm"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-border">
                      <SelectItem value="wrong_specs">Incorrect fitment specs</SelectItem>
                      <SelectItem value="stolen_photos">Stolen / fake photos</SelectItem>
                      <SelectItem value="scam">Suspected scam</SelectItem>
                      <SelectItem value="duplicate">Duplicate listing</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea data-testid="report-details-input" value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} placeholder="Add details (optional)" className="bg-[#0A0A0A] border-border rounded-sm" />
                  <DialogFooter><Button data-testid="report-submit-btn" onClick={submitReport} variant="destructive" className="rounded-sm font-bold">Submit Report</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
